import React, { useEffect, useState } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import { useToast } from '../../components/common/Toast';
import { adService } from '../../services/ad.service';
import { theme } from '../../config/theme';
import { formatDate } from '../../utils/helpers';
import { getImageUrl } from '../../utils/productHelpers';
import {
  TrashIcon,
  EditIcon,
  PlusIcon,
  SearchIcon,
  ImageIcon,
  CheckIcon,
  XIcon,
} from '../../components/icons/Icons';

const AdManagement = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('ads');
  const [loading, setLoading] = useState(true);

  // Ads state
  const [ads, setAds] = useState([]);
  const [adsPagination, setAdsPagination] = useState(null);
  const [adsPage, setAdsPage] = useState(1);
  const [placementFilter, setPlacementFilter] = useState('');

  // Giveaway Entries state
  const [entries, setEntries] = useState([]);
  const [entriesPagination, setEntriesPagination] = useState(null);
  const [entriesPage, setEntriesPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal State
  const [isAdModalOpen, setIsAdModalOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);

  // Form State
  const [formTitle, setFormTitle] = useState('');
  const [formLinkUrl, setFormLinkUrl] = useState('');
  const [formPlacement, setFormPlacement] = useState('homepage_banner');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formImageFile, setFormImageFile] = useState(null);
  const [formImagePreview, setFormImagePreview] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Giveaway Settings Form State
  const [giveawayTitle, setGiveawayTitle] = useState('');
  const [giveawayDesc, setGiveawayDesc] = useState('');
  const [giveawayImageFile, setGiveawayImageFile] = useState(null);
  const [giveawayImagePreview, setGiveawayImagePreview] = useState('');
  const [giveawaySubmitting, setGiveawaySubmitting] = useState(false);
  const [deleteGiveawayImageFlag, setDeleteGiveawayImageFlag] = useState(false);

  // Summary Stats
  const [stats, setStats] = useState({
    totalAds: 0,
    activeAds: 0,
    totalEntries: 0
  });

  const fetchAds = async (page = 1) => {
    try {
      const filters = {};
      if (placementFilter) filters.placement = placementFilter;

      const response = await adService.getAll(page, 10, filters);
      const payload = response?.data || response;
      setAds(payload?.ads || []);
      setAdsPagination(payload?.pagination || null);

      // Update stats based on results
      if (payload?.pagination) {
        setStats(prev => ({
          ...prev,
          totalAds: payload.pagination.total
        }));
      }
    } catch (error) {
      console.error('Failed to fetch ads:', error);
      toast.error('Failed to load ads');
    }
  };

  const fetchGiveawayEntries = async (page = 1) => {
    try {
      const response = await adService.getGiveawayEntries(page, 15, searchQuery);
      const payload = response?.data || response;
      setEntries(payload?.entries || []);
      setEntriesPagination(payload?.pagination || null);

      if (payload?.pagination) {
        setStats(prev => ({
          ...prev,
          totalEntries: payload.pagination.total
        }));
      }
    } catch (error) {
      console.error('Failed to fetch giveaway entries:', error);
      toast.error('Failed to load giveaway entries');
    }
  };

  const fetchGiveawaySettings = async () => {
    try {
      const response = await adService.getGiveawaySettings();
      const payload = response?.data || response;
      setGiveawayTitle(payload?.title || '');
      setGiveawayDesc(payload?.description || '');
      setGiveawayImagePreview(payload?.image_url ? getImageUrl(payload.image_url) : '');
    } catch (error) {
      console.error('Failed to fetch giveaway settings:', error);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAds(adsPage),
        fetchGiveawayEntries(entriesPage),
        fetchGiveawaySettings()
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchAds(adsPage);
    }
  }, [adsPage, placementFilter]);

  useEffect(() => {
    if (!loading) {
      fetchGiveawayEntries(entriesPage);
    }
  }, [entriesPage]);

  // Count active ads from the fetched ad list or fetch
  useEffect(() => {
    const activeCount = ads.filter(a => a.is_active).length;
    setStats(prev => ({
      ...prev,
      activeAds: activeCount
    }));
  }, [ads]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setEntriesPage(1);
    fetchGiveawayEntries(1);
  };

  const handleToggleAdStatus = async (adId) => {
    try {
      const response = await adService.toggleStatus(adId);
      const payload = response?.data || response;
      const updatedAd = payload?.ad || payload;

      setAds(prev => prev.map(a => a.id === adId ? { ...a, is_active: updatedAd.is_active } : a));
      toast.success(`Ad status updated successfully`);
    } catch (error) {
      console.error('Failed to toggle status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this ad banner? This action cannot be undone.')) {
      return;
    }

    try {
      await adService.delete(adId);
      setAds(prev => prev.filter(a => a.id !== adId));
      toast.success('Ad deleted successfully');
    } catch (error) {
      console.error('Failed to delete ad:', error);
      toast.error('Failed to delete ad');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this giveaway entry?')) {
      return;
    }

    try {
      await adService.deleteGiveawayEntry(entryId);
      setEntries(prev => prev.filter(e => e.id !== entryId));
      setStats(prev => ({ ...prev, totalEntries: prev.totalEntries - 1 }));
      toast.success('Giveaway entry deleted');
    } catch (error) {
      console.error('Failed to delete entry:', error);
      toast.error('Failed to delete giveaway entry');
    }
  };

  const openCreateModal = () => {
    setEditingAd(null);
    setFormTitle('');
    setFormLinkUrl('');
    setFormPlacement('homepage_banner');
    setFormIsActive(true);
    setFormImageFile(null);
    setFormImagePreview('');
    setIsAdModalOpen(true);
  };

  const openEditModal = (ad) => {
    setEditingAd(ad);
    setFormTitle(ad.title || '');
    setFormLinkUrl(ad.link_url || '');
    setFormPlacement(ad.placement || 'homepage_banner');
    setFormIsActive(ad.is_active);
    setFormImageFile(null);
    setFormImagePreview(getImageUrl(ad.image_url));
    setIsAdModalOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('title', formTitle);
      formData.append('link_url', formLinkUrl);
      formData.append('placement', formPlacement);
      formData.append('is_active', formIsActive);

      if (formImageFile) {
        formData.append('image', formImageFile);
      }

      if (editingAd) {
        await adService.update(editingAd.id, formData);
        toast.success('Ad banner updated successfully');
      } else {
        await adService.create(formData);
        toast.success('Ad banner created successfully');
      }

      setIsAdModalOpen(false);
      fetchAds(adsPage);
    } catch (error) {
      console.error('Failed to save ad:', error);
      toast.error(error.response?.data?.message || 'Failed to save ad banner');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleGiveawayImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setGiveawayImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setGiveawayImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGiveawaySettingsSubmit = async (e) => {
    e.preventDefault();
    setGiveawaySubmitting(true);

    try {
      const formData = new FormData();
      formData.append('title', giveawayTitle);
      formData.append('description', giveawayDesc);
      if (deleteGiveawayImageFlag) {
        formData.append('delete_image', 'true');
      } else if (giveawayImageFile) {
        formData.append('image', giveawayImageFile);
      }

      const response = await adService.updateGiveawaySettings(formData);
      const payload = response?.data || response;
      const data = payload?.data || payload;

      setGiveawayTitle(data.title || '');
      setGiveawayDesc(data.description || '');
      setGiveawayImagePreview(data.image_url ? getImageUrl(data.image_url) : '');
      setGiveawayImageFile(null);
      setDeleteGiveawayImageFlag(false);

      toast.success('Giveaway settings updated successfully');
    } catch (error) {
      console.error('Failed to update giveaway settings:', error);
      toast.error('Failed to update giveaway settings');
    } finally {
      setGiveawaySubmitting(false);
    }
  };

  const calculateCTR = (clicks, impressions) => {
    if (!impressions) return '0.00%';
    const ctr = (clicks / impressions) * 100;
    return `${ctr.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <MainLayout>
        <LoadingSpinner fullScreen />
      </MainLayout>
    );
  }

  const columns = [
    {
      header: 'Preview',
      accessor: 'image_url',
      width: '100px',
      render: (row) => (
        <div style={{
          width: '80px',
          height: '45px',
          borderRadius: theme.borderRadius.sm,
          overflow: 'hidden',
          backgroundColor: theme.colors.gray[100],
          border: `1px solid ${theme.colors.gray[200]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {row.image_url ? (
            <img
              src={getImageUrl(row.image_url)}
              alt={row.title || 'Ad'}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <ImageIcon size={20} color={theme.colors.gray[400]} />
          )}
        </div>
      )
    },
    {
      header: 'Title / Placement',
      accessor: 'title',
      render: (row) => (
        <div>
          <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: '2px' }}>
            {row.title || <span style={{ color: theme.colors.gray[400], fontStyle: 'italic' }}>Untitled Campaign</span>}
          </div>
          <div style={{ fontSize: '11px', color: theme.colors.text.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {row.placement.replace('_', ' ')}
          </div>
        </div>
      )
    },
    {
      header: 'Offer URL',
      accessor: 'link_url',
      render: (row) => (
        <a
          href={row.link_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: theme.colors.primary.main,
            textDecoration: 'none',
            fontSize: theme.typography.fontSize.sm,
            maxWidth: '220px',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {row.link_url || '—'}
        </a>
      )
    },
    {
      header: 'Analytics',
      accessor: 'impression_count',
      render: (row) => (
        <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
          <div>Impressions: <span style={{ color: theme.colors.text.primary, fontWeight: theme.typography.fontWeight.medium }}>{row.impression_count}</span></div>
          <div>Clicks: <span style={{ color: theme.colors.text.primary, fontWeight: theme.typography.fontWeight.medium }}>{row.click_count}</span></div>
          <div>CTR: <span style={{ color: theme.colors.primary.main, fontWeight: theme.typography.fontWeight.semibold }}>{calculateCTR(row.click_count, row.impression_count)}</span></div>
        </div>
      )
    },
    {
      header: 'Status',
      accessor: 'is_active',
      width: '120px',
      render: (row) => (
        <div onClick={(e) => { e.stopPropagation(); handleToggleAdStatus(row.id); }} style={{ cursor: 'pointer' }}>
          <Badge variant={row.is_active ? 'success' : 'danger'}>
            {row.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      )
    },
    {
      header: 'Actions',
      accessor: 'id',
      width: '100px',
      render: (row) => (
        <div style={{ display: 'flex', gap: theme.spacing.xs }} onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditModal(row)}
            style={{ padding: '6px', minWidth: 'auto' }}
            title="Edit Campaign"
          >
            <EditIcon size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteAd(row.id)}
            style={{ padding: '6px', minWidth: 'auto', color: theme.colors.error }}
            title="Delete Campaign"
          >
            <TrashIcon size={16} />
          </Button>
        </div>
      )
    }
  ];

  const entryColumns = [
    {
      header: 'Mobile Number',
      accessor: 'phone',
      render: (row) => (
        <div style={{ fontWeight: theme.typography.fontWeight.semibold, fontSize: theme.typography.fontSize.base }}>
          {row.phone}
        </div>
      )
    },
    {
      header: 'IP Address',
      accessor: 'ip_address',
      render: (row) => (
        <span style={{ fontFamily: 'monospace', color: theme.colors.text.secondary }}>
          {row.ip_address || '—'}
        </span>
      )
    },
    {
      header: 'Submitted Date',
      accessor: 'created_at',
      render: (row) => (
        <div style={{ color: theme.colors.text.secondary }}>
          {formatDate(row.created_at, 'long')}
        </div>
      )
    },
    {
      header: 'Actions',
      accessor: 'id',
      width: '100px',
      render: (row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteEntry(row.id)}
            style={{ padding: '6px', minWidth: 'auto', color: theme.colors.error }}
            title="Delete Entry"
          >
            <TrashIcon size={18} />
          </Button>
        </div>
      )
    }
  ];

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: 1200, margin: '0 auto' }}>
        
        {/* Header section */}
        <div style={{
          display: 'flex',
          justifyContent: 'between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: theme.spacing.md,
          marginBottom: theme.spacing.xl
        }}>
          <div>
            <h1 style={{
              fontSize: theme.typography.fontSize['3xl'],
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colors.text.primary,
              marginBottom: theme.spacing.xs
            }}>
              Ad & Giveaways Management
            </h1>
            <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
              Create ad banners, configure giveaway links, and view lucky mobile participants.
            </p>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Button variant="primary" size="md" onClick={openCreateModal} style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
              <PlusIcon size={18} /> Create Ad Banner
            </Button>
          </div>
        </div>

        {/* Quick Stats overview */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: theme.spacing.lg,
          marginBottom: theme.spacing.xl
        }}>
          <Card padding="lg" style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg, background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)', border: 'none' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: theme.colors.primary.main, display: 'flex', alignItems: 'center', justify: 'center', color: theme.colors.white }}>
              <ImageIcon size={24} />
            </div>
            <div>
              <div style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm }}>Total Ad Campaigns</div>
              <div style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary }}>
                {stats.totalAds} <span style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.success }}>({stats.activeAds} Active)</span>
              </div>
            </div>
          </Card>

          <Card padding="lg" style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg, background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', border: 'none' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: theme.colors.success, display: 'flex', alignItems: 'center', justify: 'center', color: theme.colors.white }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
            </div>
            <div>
              <div style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm }}>Giveaway Entries</div>
              <div style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary }}>
                {stats.totalEntries} Phone Numbers
              </div>
            </div>
          </Card>
        </div>

        {/* Tab Controls */}
        <Card style={{ marginBottom: theme.spacing.lg, padding: '4px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setActiveTab('ads')}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                background: activeTab === 'ads' ? theme.colors.primary.main : 'transparent',
                color: activeTab === 'ads' ? theme.colors.white : theme.colors.text.secondary,
                fontWeight: theme.typography.fontWeight.bold,
                borderRadius: theme.borderRadius.md,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              Ad Banners & Offers
            </button>
            <button
              onClick={() => setActiveTab('giveaway')}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                background: activeTab === 'giveaway' ? theme.colors.primary.main : 'transparent',
                color: activeTab === 'giveaway' ? theme.colors.white : theme.colors.text.secondary,
                fontWeight: theme.typography.fontWeight.bold,
                borderRadius: theme.borderRadius.md,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              Giveaway Campaign Config
            </button>
            <button
              onClick={() => setActiveTab('entries')}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                background: activeTab === 'entries' ? theme.colors.primary.main : 'transparent',
                color: activeTab === 'entries' ? theme.colors.white : theme.colors.text.secondary,
                fontWeight: theme.typography.fontWeight.bold,
                borderRadius: theme.borderRadius.md,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              Giveaway Mobile Submissions
            </button>
          </div>
        </Card>

        {/* Tab Content */}
        {activeTab === 'ads' ? (
          <div>
            <Card padding="xl">
              <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing.md, marginBottom: theme.spacing.lg }}>
                <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, margin: 0 }}>
                  Active Promotion Slots
                </h3>
              </div>

              <Table
                columns={columns}
                data={ads}
                emptyMessage="No advertisements configured yet. Click 'Create Ad Banner' to upload your first offer campaign!"
              />

              {adsPagination && adsPagination.totalPages > 1 && (
                <Pagination
                  currentPage={adsPagination.page}
                  totalPages={adsPagination.totalPages}
                  hasNextPage={adsPagination.page < adsPagination.totalPages}
                  hasPreviousPage={adsPagination.page > 1}
                  totalItems={adsPagination.total}
                  itemsPerPage={adsPagination.limit}
                  onPageChange={(page) => setAdsPage(page)}
                />
              )}
            </Card>
          </div>
        ) : activeTab === 'giveaway' ? (
          <div>
            <Card padding="xl">
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.lg }}>
                Giveaway Engagement Settings
              </h3>
              
              <form onSubmit={handleGiveawaySettingsSubmit} style={{ maxWidth: '600px' }}>
                <Input
                  label="Giveaway Heading"
                  placeholder="e.g. Enter your phone number here to participate and win exciting gifts"
                  value={giveawayTitle}
                  onChange={(e) => setGiveawayTitle(e.target.value)}
                  name="giveaway_title"
                  required
                />

                <Input
                  label="Giveaway Description / Subtitle"
                  placeholder="e.g. Join the AlarcTrade giveaway! Lucky winners will be contacted directly."
                  value={giveawayDesc}
                  onChange={(e) => setGiveawayDesc(e.target.value)}
                  name="giveaway_desc"
                  required
                />

                {/* Giveaway Image */}
                <div style={{ marginBottom: theme.spacing.xl }}>
                  <label style={{ display: 'block', marginBottom: theme.spacing.xs, fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
                    Giveaway Prize / Gift Image
                  </label>
                  
                  <div style={{ display: 'flex', gap: theme.spacing.md, alignItems: 'center' }}>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: theme.borderRadius.md,
                      border: `1px dashed ${theme.colors.gray[300]}`,
                      backgroundColor: theme.colors.gray[50],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}>
                      {giveawayImagePreview ? (
                        <img src={giveawayImagePreview} alt="Giveaway Preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <ImageIcon size={40} color={theme.colors.gray[400]} />
                      )}
                    </div>

                    <div style={{ flex: 1 }}>
                      <input
                        type="file"
                        id="giveawayImageFileInput"
                        accept="image/*"
                        onChange={handleGiveawayImageChange}
                        style={{ display: 'none' }}
                      />
                      <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('giveawayImageFileInput').click()}
                        >
                          Change Image
                        </Button>
                        {giveawayImagePreview && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              setGiveawayImageFile(null);
                              setGiveawayImagePreview('');
                              setDeleteGiveawayImageFlag(true);
                            }}
                          >
                            Remove Image
                          </Button>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: theme.colors.text.secondary, marginTop: '4px' }}>
                        JPG, PNG, WEBP. Max size 5MB. If none is uploaded, the default gift box image will be displayed.
                      </div>
                    </div>
                  </div>
                </div>

                <Button type="submit" variant="primary" disabled={giveawaySubmitting}>
                  {giveawaySubmitting ? 'Saving settings...' : 'Save Settings'}
                </Button>
              </form>
            </Card>
          </div>
        ) : (
          <div>
            <Card padding="xl">
              {/* Search Control */}
              <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.lg, maxWidth: '500px' }}>
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  border: `1px solid ${theme.colors.gray[300]}`,
                  borderRadius: theme.borderRadius.md,
                  padding: '4px 12px',
                  backgroundColor: theme.colors.white
                }}>
                  <SearchIcon size={18} color={theme.colors.gray[400]} style={{ marginRight: '8px' }} />
                  <input
                    type="text"
                    placeholder="Search by phone number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      border: 'none',
                      outline: 'none',
                      flex: 1,
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.colors.text.primary,
                      padding: '8px 0',
                    }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => { setSearchQuery(''); setEntriesPage(1); setTimeout(() => fetchGiveawayEntries(1), 50); }}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: theme.colors.text.secondary }}
                    >
                      <XIcon size={16} />
                    </button>
                  )}
                </div>
                <Button type="submit" variant="primary">Search</Button>
              </form>

              <Table
                columns={entryColumns}
                data={entries}
                emptyMessage={searchQuery ? "No entries match your search query." : "No phone numbers have been submitted to the giveaway yet."}
              />

              {entriesPagination && entriesPagination.totalPages > 1 && (
                <Pagination
                  currentPage={entriesPagination.page}
                  totalPages={entriesPagination.totalPages}
                  hasNextPage={entriesPagination.page < entriesPagination.totalPages}
                  hasPreviousPage={entriesPagination.page > 1}
                  totalItems={entriesPagination.total}
                  itemsPerPage={entriesPagination.limit}
                  onPageChange={(page) => setEntriesPage(page)}
                />
              )}
            </Card>
          </div>
        )}

        {/* Edit/Create Ad Modal */}
        <Modal
          isOpen={isAdModalOpen}
          onClose={() => setIsAdModalOpen(false)}
          title={editingAd ? 'Edit Ad Campaign' : 'Create Ad Campaign'}
        >
          <form onSubmit={handleFormSubmit}>
            <Input
              label="Campaign Title"
              placeholder="e.g. 50% Off Summer Sale!"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              name="title"
            />

            <Input
              label="Offer URL (Redirect Link)"
              placeholder="e.g. https://partner-site.com/giveaway-offer"
              value={formLinkUrl}
              onChange={(e) => setFormLinkUrl(e.target.value)}
              name="link_url"
              type="url"
            />

            {/* Image upload */}
            <div style={{ marginBottom: theme.spacing.lg }}>
              <label style={{ display: 'block', marginBottom: theme.spacing.xs, fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium }}>
                Ad Image Upload
              </label>

              <div style={{ display: 'flex', gap: theme.spacing.md, alignItems: 'center' }}>
                <div style={{
                  width: '240px',
                  height: '40px',
                  borderRadius: theme.borderRadius.md,
                  border: `1px dashed ${theme.colors.gray[300]}`,
                  backgroundColor: theme.colors.gray[50],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}>
                  {formImagePreview ? (
                    <img src={formImagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <ImageIcon size={20} color={theme.colors.gray[400]} />
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <input
                    type="file"
                    id="adImageFileInput"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('adImageFileInput').click()}
                  >
                    Select File
                  </Button>
                  <div style={{ fontSize: '11px', color: theme.colors.text.secondary, marginTop: '4px' }}>
                    Recommended size: 1200 x 200 px (6:1 aspect ratio). JPG, PNG, WEBP. Max 5MB.
                  </div>
                </div>
              </div>
            </div>

            {/* Toggle Status switch */}
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: theme.spacing.xl }}>
              <input
                type="checkbox"
                id="formIsActiveCheckbox"
                checked={formIsActive}
                onChange={(e) => setFormIsActive(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: theme.colors.primary.main,
                  cursor: 'pointer'
                }}
              />
              <label htmlFor="formIsActiveCheckbox" style={{ fontSize: theme.typography.fontSize.sm, cursor: 'pointer', fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
                Publish immediately (Set Active)
              </label>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
              <Button type="button" variant="outline" onClick={() => setIsAdModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={formSubmitting}>
                {formSubmitting ? 'Saving...' : editingAd ? 'Update Campaign' : 'Publish Campaign'}
              </Button>
            </div>
          </form>
        </Modal>

      </div>
    </MainLayout>
  );
};

export default AdManagement;
